// First Created - 08 Oct 2026
// Author - Atul Pandey
// Purpose - Admin List Page for Session Reference Content
//
// Updated - 11 Oct 2026
// - Added CSV Bulk Import for YouTube Reference Content
// - Added Trinity Session selection for imported content
// - Added CSV validation and preview
// - Added dynamic Prompt1, Prompt2, Prompt3... support
// - Added duplicate detection for Session + YouTube Video ID
// - Retained existing list, search, filters and CRUD actions


import React, {
    useMemo,
    useState,
} from "react"

import useTitle from "@/hooks/useTitle"

import {
    addIcon,
} from "@/assets/img/imgAssets"

import {
    PencilSquareIcon,
    TrashIcon,
    MagnifyingGlassIcon,
    ArrowUpTrayIcon,
    XMarkIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
} from "@heroicons/react/24/outline"

import {
    PlayCircleIcon,
    DocumentTextIcon,
    BookOpenIcon,
    LinkIcon,
} from "@heroicons/react/24/solid"

import PulseLoader from "react-spinners/PulseLoader"

import {
    useGetSessionsQuery,
} from "@/features/sessions/sessionsApiSlice"

import {
    useGetSessionReferenceContentQuery,
    useCreateSessionReferenceContentMutation,
} from "./sessionReferenceContentApiSlice"


/*
|--------------------------------------------------------------------------
| 01 - Helpers
|--------------------------------------------------------------------------
*/

const getRefId = value => {

    if (!value) return ""

    if (typeof value === "string") {
        return value
    }

    return value?._id || value?.id || ""

}


const toList = data => {

    return (data?.ids || [])
        .map(id => data.entities[id])
        .filter(Boolean)

}


const contentTypeIcon = {
    Video: PlayCircleIcon,
    Article: DocumentTextIcon,
    Blog: BookOpenIcon,
    Other: LinkIcon,
}


/*
|--------------------------------------------------------------------------
| 01a - CSV Parsing
|--------------------------------------------------------------------------
|
| Supports:
| - UTF-8 BOM
| - Quoted CSV fields
| - Escaped double quotes
| - Commas within quoted fields
| - Multiline quoted descriptions and prompts
|
|--------------------------------------------------------------------------
*/

const parseCSV = text => {

    const rows = []

    let row = []
    let field = ""
    let inQuotes = false

    const input =
        String(text || "").replace(/^\uFEFF/, "")

    for (let i = 0; i < input.length; i++) {

        const char = input[i]

        if (char === '"') {

            if (
                inQuotes &&
                input[i + 1] === '"'
            ) {

                field += '"'
                i++

            } else if (
                inQuotes ||
                field.length === 0
            ) {

                inQuotes = !inQuotes

            } else {

                field += char

            }

        } else if (
            char === "," &&
            !inQuotes
        ) {

            row.push(field)
            field = ""

        } else if (
            (char === "\n" || char === "\r") &&
            !inQuotes
        ) {

            if (
                char === "\r" &&
                input[i + 1] === "\n"
            ) {
                i++
            }

            row.push(field)

            if (
                row.some(
                    value => String(value).trim() !== ""
                )
            ) {

                rows.push(row)

            }

            row = []
            field = ""

        } else {

            field += char

        }

    }

    if (inQuotes) {

        throw new Error(
            "Invalid CSV: an opening quotation mark was not closed."
        )

    }

    row.push(field)

    if (
        row.some(
            value => String(value).trim() !== ""
        )
    ) {

        rows.push(row)

    }

    if (!rows.length) {
        return []
    }

    const headers = rows[0].map(
        header => String(header).trim()
    )

    if (
        headers.some(header => !header)
    ) {

        throw new Error(
            "Invalid CSV: one or more column headers are empty."
        )

    }

    const normalizedHeaders =
        headers.map(
            header =>
                header.toLowerCase()
        )

    if (
        new Set(normalizedHeaders).size !==
        normalizedHeaders.length
    ) {

        throw new Error(
            "Invalid CSV: duplicate column headers were found."
        )

    }

    return rows.slice(1).map(
        (values, index) => {

            const record = {}

            headers.forEach(
                (header, headerIndex) => {

                    record[header] =
                        String(
                            values[headerIndex] ?? ""
                        ).trim()

                }
            )

            return {
                ...record,
                __rowNumber: index + 2,
            }

        }
    )

}


/*
|--------------------------------------------------------------------------
| 01b - CSV Field Lookup
|--------------------------------------------------------------------------
|
| Header lookup is case insensitive.
|
|--------------------------------------------------------------------------
*/

const getCSVField = (
    row,
    fieldName
) => {

    const key = Object.keys(row).find(
        header =>
            header.toLowerCase() ===
            fieldName.toLowerCase()
    )

    return key
        ? String(row[key] ?? "").trim()
        : ""

}


/*
|--------------------------------------------------------------------------
| 01c - YouTube Video ID Extraction
|--------------------------------------------------------------------------
*/

const getYouTubeVideoId = value => {

    const input =
        String(value || "").trim()

    if (
        /^[a-zA-Z0-9_-]{11}$/.test(input)
    ) {
        return input
    }

    try {

        const url = new URL(input)

        const hostname =
            url.hostname.toLowerCase()

        let videoId = ""

        if (
            hostname === "youtu.be" ||
            hostname === "www.youtu.be"
        ) {

            videoId =
                url.pathname.split("/")[1] || ""

        } else if (
            hostname === "youtube.com" ||
            hostname === "www.youtube.com" ||
            hostname === "m.youtube.com"
        ) {

            if (
                url.pathname === "/watch"
            ) {

                videoId =
                    url.searchParams.get("v") || ""

            } else if (
                url.pathname.startsWith("/shorts/") ||
                url.pathname.startsWith("/embed/") ||
                url.pathname.startsWith("/live/")
            ) {

                videoId =
                    url.pathname.split("/")[2] || ""

            }

        }

        return /^[a-zA-Z0-9_-]{11}$/.test(videoId)
            ? videoId
            : ""

    } catch (error) {

        return ""

    }

}


/*
|--------------------------------------------------------------------------
| 01d - Build Description
|--------------------------------------------------------------------------
|
| CSV Description is followed by:
|
| Session #: ...
| Part: ...
| Duration: ...
|
| Session # is content metadata.
| It is not the Trinity system sessionId.
|
|--------------------------------------------------------------------------
*/

const buildDescription = row => {

    const description =
        getCSVField(row, "Description")

    const sessionNumber =
        getCSVField(row, "Session #")

    const part =
        getCSVField(row, "Part")

    const duration =
        getCSVField(row, "Duration")

    const metadata = []

    if (sessionNumber) {

        metadata.push(
            `Session #: ${sessionNumber}`
        )

    }

    if (part) {

        metadata.push(
            `Part: ${part}`
        )

    }

    if (duration) {

        metadata.push(
            `Duration: ${duration}`
        )

    }

    return [
        description,
        metadata.join("\n"),
    ]
        .filter(Boolean)
        .join("\n\n")

}


/*
|--------------------------------------------------------------------------
| 01e - Build Reflection Prompts
|--------------------------------------------------------------------------
|
| Supports any number of columns:
|
| Prompt1, Prompt2, Prompt3, Prompt4...
|
| Empty prompts are ignored.
|
|--------------------------------------------------------------------------
*/

const buildReflectionPrompts = row => {

    return Object.keys(row)
        .filter(
            key =>
                /^Prompt\d+$/i.test(key)
        )
        .sort(
            (a, b) => {

                const numberA =
                    Number(a.match(/\d+/)[0])

                const numberB =
                    Number(b.match(/\d+/)[0])

                return numberA - numberB

            }
        )
        .map(
            key =>
                String(row[key] || "").trim()
        )
        .filter(Boolean)
        .map(
            (promptText, index) => ({

                promptText,

                displayOrder: index + 1,

                active: true,

            })
        )

}


/*
|--------------------------------------------------------------------------
| 01f - Prepare Import Record
|--------------------------------------------------------------------------
*/

const prepareImportRow = (
    row,
    index
) => {

    const title =
        getCSVField(
            row,
            "Session Title"
        )

    const youtubeLink =
        getCSVField(
            row,
            "YouTube Link"
        )

    const videoId =
        getYouTubeVideoId(
            youtubeLink
        )

    const orderText =
        getCSVField(
            row,
            "#"
        )

    const parsedOrder =
        Number(orderText)

    const displayOrder =
        orderText &&
        Number.isSafeInteger(parsedOrder) &&
        parsedOrder >= 0
            ? parsedOrder
            : index + 1

    const errors = []

    if (!title) {

        errors.push(
            "Session Title is missing."
        )

    }

    if (!videoId) {

        errors.push(
            "YouTube Link is missing or invalid."
        )

    }

    if (
        orderText &&
        (
            !Number.isSafeInteger(parsedOrder) ||
            parsedOrder < 0
        )
    ) {

        errors.push(
            "Sequence # must be a nonnegative whole number."
        )

    }

    return {

        csvRowNumber:
            row.__rowNumber || index + 2,

        title,

        description:
            buildDescription(row),

        videoId,

        displayOrder,

        reflectionPrompts:
            buildReflectionPrompts(row),

        errors,

    }

}



/*
|--------------------------------------------------------------------------
| 02 - Main Component
|--------------------------------------------------------------------------
*/

const EtSessionReferenceContentList = ({
    onAdd,
    onUpdate,
    onDelete,
}) => {

    useTitle(
        "Session Reference Content"
    )


    /*
    |--------------------------------------------------------------------------
    | 03 - Filter State
    |--------------------------------------------------------------------------
    */

    const [
        searchText,
        setSearchText,
    ] = useState("")

    const [
        selectedSessionId,
        setSelectedSessionId,
    ] = useState("")

    const [
        selectedContentType,
        setSelectedContentType,
    ] = useState("All")

    const [
        selectedStatus,
        setSelectedStatus,
    ] = useState("All")


    /*
    |--------------------------------------------------------------------------
    | 04 - CSV Import State
    |--------------------------------------------------------------------------
    */

    const [
        showBulkImport,
        setShowBulkImport,
    ] = useState(false)

    const [
        importSessionId,
        setImportSessionId,
    ] = useState("")

    const [
        importFileName,
        setImportFileName,
    ] = useState("")

    const [
        importRows,
        setImportRows,
    ] = useState([])

    const [
        importError,
        setImportError,
    ] = useState("")

    const [
        importing,
        setImporting,
    ] = useState(false)

    const [
        importProgress,
        setImportProgress,
    ] = useState(null)

    const [
        importResults,
        setImportResults,
    ] = useState([])


    /*
    |--------------------------------------------------------------------------
    | 05 - Queries
    |--------------------------------------------------------------------------
    */

    const {
        data: contentData,
        isLoading,
        isError,
        error,
    } = useGetSessionReferenceContentQuery()

    const {
        data: sessionsData,
    } = useGetSessionsQuery(
        "sessionsList"
    )


    /*
    |--------------------------------------------------------------------------
    | 06 - Create Mutation
    |--------------------------------------------------------------------------
    */

    const [
        createSessionReferenceContent,
    ] = useCreateSessionReferenceContentMutation()


    /*
    |--------------------------------------------------------------------------
    | 07 - Normalize Collections
    |--------------------------------------------------------------------------
    */

    const referenceContent = useMemo(
        () => toList(contentData),
        [contentData]
    )

    const sessions = useMemo(
        () => toList(sessionsData),
        [sessionsData]
    )


    /*
    |--------------------------------------------------------------------------
    | 08 - Session Lookup
    |--------------------------------------------------------------------------
    */

    const sessionMap = useMemo(() => {

        const map = new Map()

        sessions.forEach(session => {

            map.set(
                getRefId(session),
                session
            )

        })

        return map

    }, [sessions])


    const getSessionName = sessionRef => {

        if (
            sessionRef &&
            typeof sessionRef === "object" &&
            sessionRef.sessionName
        ) {

            return sessionRef.sessionName

        }

        const session =
            sessionMap.get(
                getRefId(sessionRef)
            )

        return (
            session?.sessionName ||
            "Session not defined"
        )

    }


    /*
    |--------------------------------------------------------------------------
    | 09 - Session Selector Options
    |--------------------------------------------------------------------------
    */

    const sessionOptions = useMemo(() => {

        return sessions
            .filter(
                session =>
                    session.active !== false
            )
            .slice()
            .sort(
                (a, b) => {

                    const trailA =
                        getRefId(a.trailId)

                    const trailB =
                        getRefId(b.trailId)

                    if (trailA !== trailB) {
                        return trailA.localeCompare(trailB)
                    }

                    return (
                        a.sessionSequence ?? 999999
                    ) - (
                        b.sessionSequence ?? 999999
                    )

                }
            )

    }, [sessions])


    /*
    |--------------------------------------------------------------------------
    | 10 - Counts
    |--------------------------------------------------------------------------
    */

    const counts = useMemo(
        () => ({

            all:
                referenceContent.length,

            active:
                referenceContent.filter(
                    item =>
                        item.active !== false
                ).length,

            inactive:
                referenceContent.filter(
                    item =>
                        item.active === false
                ).length,

        }),
        [referenceContent]
    )


    /*
    |--------------------------------------------------------------------------
    | 11 - Filter And Sort Content
    |--------------------------------------------------------------------------
    */

    const filteredContent = useMemo(() => {

        const search =
            searchText.trim().toLowerCase()

        return referenceContent
            .filter(item => {

                if (
                    selectedSessionId &&
                    getRefId(item.sessionId) !==
                    selectedSessionId
                ) {
                    return false
                }

                if (
                    selectedContentType !== "All" &&
                    item.contentType !==
                    selectedContentType
                ) {
                    return false
                }

                if (
                    selectedStatus === "Active" &&
                    item.active === false
                ) {
                    return false
                }

                if (
                    selectedStatus === "Inactive" &&
                    item.active !== false
                ) {
                    return false
                }

                const searchable = [

                    item.title,
                    item.description,
                    item.contentProvider,
                    item.videoId,
                    item.contentUrl,
                    getSessionName(item.sessionId),

                ]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase()

                return (
                    !search ||
                    searchable.includes(search)
                )

            })
            .sort((a, b) => {

                const sessionComparison =
                    getSessionName(
                        a.sessionId
                    ).localeCompare(
                        getSessionName(b.sessionId)
                    )

                if (sessionComparison !== 0) {
                    return sessionComparison
                }

                const orderComparison =
                    (a.displayOrder ?? 0) -
                    (b.displayOrder ?? 0)

                if (orderComparison !== 0) {
                    return orderComparison
                }

                return (
                    a.title || ""
                ).localeCompare(
                    b.title || ""
                )

            })

    }, [
        referenceContent,
        searchText,
        selectedSessionId,
        selectedContentType,
        selectedStatus,
        sessionMap,
    ])


    /*
    |--------------------------------------------------------------------------
    | 12 - CSV Import Validation
    |--------------------------------------------------------------------------
    |
    | Validation is performed against:
    |
    | - Missing title
    | - Invalid YouTube link
    | - Duplicate Video ID within the CSV
    | - Existing Video ID for the chosen Trinity Session
    |
    |--------------------------------------------------------------------------
    */

    const validatedImportRows = useMemo(() => {

        const seenVideoIds = new Set()

        const existingVideoIds = new Set(

            referenceContent
                .filter(
                    item =>
                        getRefId(item.sessionId) ===
                        importSessionId
                )
                .map(
                    item =>
                        item.videoId
                )
                .filter(Boolean)

        )

        return importRows.map(row => {

            const errors = [
                ...row.errors,
            ]

            let isDuplicate = false

            if (row.videoId) {

                if (
                    seenVideoIds.has(row.videoId)
                ) {

                    isDuplicate = true

                } else {

                    seenVideoIds.add(
                        row.videoId
                    )

                }

                if (
                    existingVideoIds.has(row.videoId)
                ) {

                    isDuplicate = true

                }

            }

            return {

                ...row,

                errors,

                isDuplicate,

                canImport:
                    errors.length === 0 &&
                    !isDuplicate,

            }

        })

    }, [
        importRows,
        importSessionId,
        referenceContent,
    ])


    const importCounts = useMemo(
        () => ({

            total:
                validatedImportRows.length,

            valid:
                validatedImportRows.filter(
                    row => row.canImport
                ).length,

            invalid:
                validatedImportRows.filter(
                    row =>
                        row.errors.length > 0
                ).length,

            duplicate:
                validatedImportRows.filter(
                    row =>
                        row.isDuplicate
                ).length,

        }),
        [validatedImportRows]
    )


    /*
    |--------------------------------------------------------------------------
    | 13 - Reset Import
    |--------------------------------------------------------------------------
    */

    const resetImport = () => {

        if (importing) return

        setImportFileName("")
        setImportRows([])
        setImportError("")
        setImportProgress(null)
        setImportResults([])

    }


    /*
    |--------------------------------------------------------------------------
    | 14 - Open / Close Bulk Import
    |--------------------------------------------------------------------------
    */

    const toggleBulkImport = () => {

        if (importing) return

        setShowBulkImport(previous => {

            if (previous) {

                resetImport()

            } else {

                setImportSessionId(
                    selectedSessionId || ""
                )

            }

            return !previous

        })

    }


    /*
    |--------------------------------------------------------------------------
    | 15 - Read CSV File
    |--------------------------------------------------------------------------
    */

    const handleCSVFileChange = async event => {

        const file =
            event.target.files?.[0]

        event.target.value = ""

        if (!file || importing) {
            return
        }

        resetImport()

        if (
            !file.name.toLowerCase().endsWith(".csv")
        ) {

            setImportError(
                "Please select a CSV file."
            )

            return

        }

        try {

            const text =
                await file.text()

            const parsedRows =
                parseCSV(text)

            if (
                parsedRows.length === 0
            ) {

                throw new Error(
                    "The CSV contains no data rows."
                )

            }

            const firstRow =
                parsedRows[0]

            const requiredColumns = [
                "Session Title",
                "YouTube Link",
            ]

            const headers =
                Object.keys(firstRow)
                    .map(
                        key =>
                            key.toLowerCase()
                    )

            const missingColumns =
                requiredColumns.filter(
                    column =>
                        !headers.includes(
                            column.toLowerCase()
                        )
                )

            if (
                missingColumns.length > 0
            ) {

                throw new Error(
                    `Missing CSV columns: ${missingColumns.join(", ")}`
                )

            }

            const preparedRows =
                parsedRows.map(
                    (row, index) =>
                        prepareImportRow(
                            row,
                            index
                        )
                )

            setImportRows(
                preparedRows
            )

            setImportFileName(
                file.name
            )

        } catch (error) {

            setImportError(
                error.message ||
                "Unable to read CSV file."
            )

        }

    }


    /*
    |--------------------------------------------------------------------------
    | 16 - Import Records
    |--------------------------------------------------------------------------
    |
    | Uses the existing single record creation mutation.
    |
    | Creates only validated, nonduplicate content.
    |
    | Runs sequentially to provide row level results.
    |
    |--------------------------------------------------------------------------
    */

    const handleImport = async () => {

        if (
            importing ||
            !importSessionId
        ) {
            return
        }

        const rowsToImport =
            validatedImportRows.filter(
                row => row.canImport
            )

        if (
            rowsToImport.length === 0
        ) {
            return
        }

        const results = []

        setImporting(true)
        setImportError("")
        setImportResults([])

        setImportProgress({
            completed: 0,
            total: rowsToImport.length,
        })

        try {

            for (
                let index = 0;
                index < rowsToImport.length;
                index++
            ) {

                const row =
                    rowsToImport[index]

                const payload = {

                    sessionId:
                        importSessionId,

                    contentType:
                        "Video",

                    contentProvider:
                        "YouTube",

                    title:
                        row.title,

                    description:
                        row.description,

                    videoId:
                        row.videoId,

                    contentUrl:
                        "",

                    displayOrder:
                        row.displayOrder,

                    reflectionPrompts:
                        row.reflectionPrompts,

                    active:
                        true,

                }

                try {

                    await createSessionReferenceContent(
                        payload
                    ).unwrap()

                    results.push({

                        csvRowNumber:
                            row.csvRowNumber,

                        title:
                            row.title,

                        status:
                            "Created",

                        message:
                            "Created successfully.",

                    })

                } catch (error) {

                    results.push({

                        csvRowNumber:
                            row.csvRowNumber,

                        title:
                            row.title,

                        status:
                            "Failed",

                        message:
                            error?.data?.message ||
                            error?.message ||
                            "Unable to create content.",

                    })

                }

                setImportProgress({

                    completed: index + 1,

                    total:
                        rowsToImport.length,

                })

            }

        } finally {

            setImportResults(results)

            setImporting(false)

        }

    }


    /*
    |--------------------------------------------------------------------------
    | 17 - Loading / Error
    |--------------------------------------------------------------------------
    */

    if (isLoading) {

        return (
            <PulseLoader color="#ea580c" />
        )

    }

    if (isError) {

        return (

            <p className="text-sm text-orange-700 m-2">

                {error?.data?.message ||
                    "Unable to load Session Reference Content."}

            </p>

        )

    }


    /*
    |--------------------------------------------------------------------------
    | 18 - Render
    |--------------------------------------------------------------------------
    */

    return (

        <div className="bg-gray-200 m-1 p-1 rounded font-poppins mx-auto">


            {/*
            |--------------------------------------------------------------------------
            | Header
            |--------------------------------------------------------------------------
            */}

            <div className="flex flex-wrap justify-between items-center gap-2 m-1 p-1">

                <div className="flex flex-wrap items-center gap-2">

                    <p className="text-2xl m-1 p-1 font-bold">

                        Session Reference Content

                    </p>

                    <img
                        src={addIcon}
                        alt="Add Reference Content"
                        className="h-10 w-10 cursor-pointer"
                        onClick={onAdd}
                    />


                    {/*
                    |--------------------------------------------------------------------------
                    | Bulk Import Button
                    |--------------------------------------------------------------------------
                    */}

                    <button
                        type="button"
                        onClick={toggleBulkImport}
                        disabled={importing}
                        className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 border border-orange-300 rounded-full px-4 py-2 text-xs font-bold hover:bg-orange-200 disabled:opacity-50"
                    >

                        <ArrowUpTrayIcon
                            className="h-4 w-4"
                        />

                        Bulk Import CSV

                    </button>

                </div>


                <p className="text-xs bg-white border border-orange-600 text-orange-600 rounded-full px-3 py-1">

                    Total: {referenceContent.length}

                </p>

            </div>



            {/*
            |--------------------------------------------------------------------------
            | 19 - Bulk CSV Import Panel
            |--------------------------------------------------------------------------
            */}

            {showBulkImport && (

                <div className="bg-white rounded-xl m-1 p-4 border border-orange-200 space-y-4">


                    {/*
                    |--------------------------------------------------------------------------
                    | Import Header
                    |--------------------------------------------------------------------------
                    */}

                    <div className="flex items-start justify-between gap-2">

                        <div>

                            <p className="text-base font-bold text-gray-800">

                                Bulk Import Reference Content

                            </p>

                            <p className="text-xs text-gray-500 mt-1">

                                Upload a CSV containing YouTube videos,
                                descriptions and optional reflection prompts.

                            </p>

                        </div>

                        <button
                            type="button"
                            onClick={toggleBulkImport}
                            disabled={importing}
                            aria-label="Close bulk import"
                            className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-50"
                        >

                            <XMarkIcon
                                className="h-5 w-5 text-gray-600"
                            />

                        </button>

                    </div>



                    {/*
                    |--------------------------------------------------------------------------
                    | Destination Trinity Session
                    |--------------------------------------------------------------------------
                    */}

                    <div className="bg-orange-50 rounded-lg p-3">

                        <label className="block text-xs font-bold text-gray-700 mb-2">

                            Destination Trinity Session

                        </label>

                        <select
                            value={importSessionId}
                            disabled={
                                importing ||
                                importResults.length > 0
                            }
                            onChange={event => {

                                setImportSessionId(
                                    event.target.value
                                )

                            }}
                            className="w-full bg-white border border-orange-200 rounded p-2 text-xs"
                        >

                            <option value="">

                                Select Trinity Session

                            </option>

                            {sessionOptions.map(
                                session => (

                                    <option
                                        key={
                                            getRefId(session)
                                        }
                                        value={
                                            getRefId(session)
                                        }
                                    >

                                        {session.sessionSequence
                                            ? `${session.sessionSequence} - `
                                            : ""}

                                        {session.sessionName}

                                    </option>

                                )
                            )}

                        </select>

                        <p className="text-[11px] text-gray-500 mt-2">

                            All imported content will belong to this
                            Trinity Session. The CSV Session # and Part
                            are included in each content description.

                        </p>

                    </div>



                    {/*
                    |--------------------------------------------------------------------------
                    | CSV File Selection
                    |--------------------------------------------------------------------------
                    */}

                    <div className="bg-gray-50 border border-dashed border-gray-300 rounded-lg p-4">

                        <label className="block text-xs font-bold text-gray-700 mb-2">

                            Select CSV File

                        </label>

                        <input
                            type="file"
                            accept=".csv,text/csv"
                            disabled={
                                importing ||
                                importResults.length > 0
                            }
                            onChange={
                                handleCSVFileChange
                            }
                            className="block w-full text-xs text-gray-600 file:mr-3 file:rounded-full file:border-0 file:bg-orange-100 file:px-3 file:py-2 file:text-orange-700 file:font-semibold disabled:opacity-50"
                        />

                        {importFileName && (

                            <p className="text-xs text-gray-600 mt-2">

                                File: {importFileName}

                            </p>

                        )}

                        <p className="text-[10px] text-gray-500 mt-2">

                            Required: Session Title, YouTube Link.
                            Optional: #, Session #, Part, Description,
                            Duration, Prompt1, Prompt2, Prompt3...

                        </p>

                    </div>



                    {/*
                    |--------------------------------------------------------------------------
                    | Import Error
                    |--------------------------------------------------------------------------
                    */}

                    {importError && (

                        <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded p-3">

                            <ExclamationTriangleIcon
                                className="h-5 w-5 text-red-500 shrink-0"
                            />

                            <p className="text-xs text-red-700">

                                {importError}

                            </p>

                        </div>

                    )}



                    {/*
                    |--------------------------------------------------------------------------
                    | 20 - CSV Preview
                    |--------------------------------------------------------------------------
                    */}

                    {validatedImportRows.length > 0 && (

                        <div className="space-y-3">


                            {/*
                            |--------------------------------------------------------------------------
                            | Preview Summary
                            |--------------------------------------------------------------------------
                            */}

                            <div className="flex flex-wrap items-center gap-2">

                                <span className="rounded-full bg-gray-100 text-gray-700 px-3 py-1 text-xs">

                                    Total: {importCounts.total}

                                </span>

                                <span className="rounded-full bg-green-100 text-green-700 px-3 py-1 text-xs">

                                    Ready: {importCounts.valid}

                                </span>

                                <span className="rounded-full bg-amber-100 text-amber-700 px-3 py-1 text-xs">

                                    Duplicates: {importCounts.duplicate}

                                </span>

                                <span className="rounded-full bg-red-100 text-red-700 px-3 py-1 text-xs">

                                    Invalid: {importCounts.invalid}

                                </span>

                            </div>



                            {/*
                            |--------------------------------------------------------------------------
                            | Preview Table
                            |--------------------------------------------------------------------------
                            */}

                            <div className="border border-gray-200 rounded-lg overflow-hidden">

                                <div className="bg-gray-100 p-3">

                                    <p className="text-xs font-bold text-gray-700">

                                        CSV Preview

                                    </p>

                                </div>

                                <div className="max-h-[420px] overflow-auto">

                                    <table className="min-w-full text-xs">

                                        <thead className="bg-gray-50 sticky top-0">

                                            <tr className="text-left text-gray-600">

                                                <th className="p-2">

                                                    Row

                                                </th>

                                                <th className="p-2">

                                                    Title / Description

                                                </th>

                                                <th className="p-2">

                                                    Video ID

                                                </th>

                                                <th className="p-2">

                                                    Prompts

                                                </th>

                                                <th className="p-2">

                                                    Status

                                                </th>

                                            </tr>

                                        </thead>

                                        <tbody>

                                            {validatedImportRows.map(
                                                row => (

                                                    <tr
                                                        key={
                                                            row.csvRowNumber
                                                        }
                                                        className="border-t border-gray-100 align-top"
                                                    >

                                                        <td className="p-2 text-gray-500">

                                                            {row.csvRowNumber}

                                                        </td>

                                                        <td className="p-2 min-w-[220px]">

                                                            <p className="font-bold text-gray-800">

                                                                {row.title || "Untitled"}

                                                            </p>

                                                            {row.description && (

                                                                <p className="text-[10px] text-gray-500 mt-2 whitespace-pre-wrap">

                                                                    {row.description}

                                                                </p>

                                                            )}

                                                        </td>

                                                        <td className="p-2 font-mono text-[10px]">

                                                            {row.videoId || "Invalid"}

                                                        </td>

                                                        <td className="p-2">

                                                            {row.reflectionPrompts.length}

                                                        </td>

                                                        <td className="p-2">

                                                            {row.errors.length > 0 ? (

                                                                <div className="text-red-600">

                                                                    Invalid

                                                                    {row.errors.map(
                                                                        (message, index) => (

                                                                            <p
                                                                                key={index}
                                                                                className="text-[10px] mt-1"
                                                                            >

                                                                                {message}

                                                                            </p>

                                                                        )
                                                                    )}

                                                                </div>

                                                            ) : row.isDuplicate ? (

                                                                <span className="text-amber-600 font-semibold">

                                                                    Duplicate - Skip

                                                                </span>

                                                            ) : (

                                                                <span className="text-green-700 font-semibold">

                                                                    Ready

                                                                </span>

                                                            )}

                                                        </td>

                                                    </tr>

                                                )
                                            )}

                                        </tbody>

                                    </table>

                                </div>

                            </div>



                            {/*
                            |--------------------------------------------------------------------------
                            | 21 - Confirm Import
                            |--------------------------------------------------------------------------
                            */}

                            {importResults.length === 0 && (

                                <div className="flex flex-wrap items-center justify-between gap-3">

                                    <p className="text-xs text-gray-500">

                                        Only valid, nonduplicate videos
                                        will be created.

                                    </p>

                                    <div className="flex items-center gap-2">

                                        <button
                                            type="button"
                                            disabled={importing}
                                            onClick={resetImport}
                                            className="px-4 py-2 rounded bg-gray-100 text-gray-700 text-xs font-semibold disabled:opacity-50"
                                        >

                                            Reset

                                        </button>

                                        <button
                                            type="button"
                                            disabled={
                                                importing ||
                                                !importSessionId ||
                                                importCounts.valid === 0
                                            }
                                            onClick={handleImport}
                                            className="inline-flex items-center gap-2 px-4 py-2 rounded bg-orange-600 text-white text-xs font-bold hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >

                                            <ArrowUpTrayIcon
                                                className="h-4 w-4"
                                            />

                                            {importing
                                                ? "Importing..."
                                                : `Import ${importCounts.valid} Records`}

                                        </button>

                                    </div>

                                </div>

                            )}



                            {/*
                            |--------------------------------------------------------------------------
                            | Import Progress
                            |--------------------------------------------------------------------------
                            */}

                            {importProgress && (

                                <div className="bg-gray-50 rounded p-3 space-y-2">

                                    <p className="text-xs font-semibold text-gray-700">

                                        {importing
                                            ? "Import in Progress"
                                            : "Import Complete"}

                                    </p>

                                    <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">

                                        <div
                                            className="h-full bg-orange-500 transition-all"
                                            style={{

                                                width:
                                                    `${importProgress.total
                                                        ? (
                                                            importProgress.completed /
                                                            importProgress.total
                                                        ) * 100
                                                        : 0}%`,

                                            }}
                                        />

                                    </div>

                                    <p className="text-[11px] text-gray-500">

                                        {importProgress.completed}
                                        {" "}of{" "}
                                        {importProgress.total}
                                        {" "}processed

                                    </p>

                                </div>

                            )}



                            {/*
                            |--------------------------------------------------------------------------
                            | 22 - Import Results
                            |--------------------------------------------------------------------------
                            */}

                            {importResults.length > 0 && (

                                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-3">

                                    <div className="flex items-center gap-2">

                                        <CheckCircleIcon
                                            className="h-5 w-5 text-green-600"
                                        />

                                        <p className="text-sm font-bold text-gray-800">

                                            Import Processing Complete

                                        </p>

                                    </div>

                                    <div className="flex flex-wrap gap-3 text-xs">

                                        <span className="text-green-700 font-bold">

                                            Created: {
                                                importResults.filter(
                                                    result =>
                                                        result.status === "Created"
                                                ).length
                                            }

                                        </span>

                                        <span className="text-red-700 font-bold">

                                            Failed: {
                                                importResults.filter(
                                                    result =>
                                                        result.status === "Failed"
                                                ).length
                                            }

                                        </span>

                                        <span className="text-amber-700 font-bold">

                                            Skipped: {
                                                importCounts.total -
                                                importResults.length
                                            }

                                        </span>

                                    </div>

                                    <div className="max-h-48 overflow-y-auto space-y-1">

                                        {importResults.map(
                                            result => (

                                                <div
                                                    key={result.csvRowNumber}
                                                    className="bg-white rounded p-2 flex items-start justify-between gap-2"
                                                >

                                                    <div className="min-w-0">

                                                        <p className="text-xs font-semibold text-gray-700">

                                                            Row {result.csvRowNumber}: {result.title}

                                                        </p>

                                                        <p className="text-[10px] text-gray-500 mt-1">

                                                            {result.message}

                                                        </p>

                                                    </div>

                                                    <span className={
                                                        result.status === "Created"
                                                            ? "text-green-700 text-xs font-bold"
                                                            : "text-red-700 text-xs font-bold"
                                                    }>

                                                        {result.status}

                                                    </span>

                                                </div>

                                            )
                                        )}

                                    </div>

                                    <button
                                        type="button"
                                        onClick={() => {

                                            resetImport()

                                        }}
                                        className="bg-orange-100 text-orange-700 rounded px-4 py-2 text-xs font-bold"
                                    >

                                        Start Another Import

                                    </button>

                                </div>

                            )}


                        </div>

                    )}


                </div>

            )}



            {/*
            |--------------------------------------------------------------------------
            | 23 - Status Chips
            |--------------------------------------------------------------------------
            */}

            <div className="flex flex-wrap gap-2 bg-white rounded m-1 p-3">

                {[
                    {
                        label: "All",
                        value: "All",
                        count: counts.all,
                    },
                    {
                        label: "Active",
                        value: "Active",
                        count: counts.active,
                    },
                    {
                        label: "Inactive",
                        value: "Inactive",
                        count: counts.inactive,
                    },
                ].map(item => (

                    <button
                        key={item.value}
                        type="button"
                        onClick={() =>
                            setSelectedStatus(
                                item.value
                            )
                        }
                        className={`rounded-full border px-3 py-1.5 text-xs ${
                            selectedStatus === item.value
                                ? "bg-orange-100 border-orange-400 text-orange-700 font-bold"
                                : "bg-gray-100 border-gray-200 text-gray-600"
                        }`}
                    >

                        {item.label} ({item.count})

                    </button>

                ))}

            </div>



            {/*
            |--------------------------------------------------------------------------
            | 24 - Search And Filters
            |--------------------------------------------------------------------------
            */}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 bg-white rounded m-1 p-3">

                <div className="relative">

                    <MagnifyingGlassIcon
                        className="absolute left-3 top-3 h-4 w-4 text-gray-400"
                    />

                    <input
                        type="text"
                        value={searchText}
                        onChange={e =>
                            setSearchText(
                                e.target.value
                            )
                        }
                        placeholder="Search reference content..."
                        className="w-full rounded bg-gray-100 py-2 pl-9 pr-3 text-xs"
                    />

                </div>


                <select
                    value={selectedSessionId}
                    onChange={e =>
                        setSelectedSessionId(
                            e.target.value
                        )
                    }
                    className="rounded bg-gray-100 p-2 text-xs"
                >

                    <option value="">

                        All Sessions

                    </option>

                    {sessionOptions.map(session => (

                        <option
                            key={getRefId(session)}
                            value={getRefId(session)}
                        >

                            {session.sessionSequence
                                ? `${session.sessionSequence} - `
                                : ""}

                            {session.sessionName}

                        </option>

                    ))}

                </select>


                <select
                    value={selectedContentType}
                    onChange={e =>
                        setSelectedContentType(
                            e.target.value
                        )
                    }
                    className="rounded bg-gray-100 p-2 text-xs"
                >

                    <option value="All">

                        All Content Types

                    </option>

                    <option value="Video">

                        Videos

                    </option>

                    <option value="Article">

                        Articles

                    </option>

                    <option value="Blog">

                        Blogs

                    </option>

                    <option value="Other">

                        Other

                    </option>

                </select>

            </div>



            {/*
            |--------------------------------------------------------------------------
            | 25 - Content List
            |--------------------------------------------------------------------------
            */}

            <div className="bg-white rounded m-1 p-2">

                <div className="flex justify-between items-center m-1 p-2">

                    <p className="text-xs font-bold">

                        Reference Content

                    </p>

                    <p className="text-[10px] text-gray-500">

                        Showing {filteredContent.length} items

                    </p>

                </div>


                {filteredContent.length === 0 && (

                    <div className="bg-gray-100 rounded m-1 p-4">

                        <p className="text-xs text-gray-600">

                            No reference content found.
                            Use the Add icon to create content
                            in the master library.

                        </p>

                    </div>

                )}



                <div className="space-y-2">

                    {filteredContent.map((item, index) => {

                        const Icon =
                            contentTypeIcon[
                                item.contentType
                            ] || LinkIcon

                        const activePromptCount =
                            (
                                item.reflectionPrompts || []
                            ).filter(
                                prompt =>
                                    prompt.active !== false
                            ).length

                        return (

                            <div
                                key={item._id}
                                className="bg-gray-100 rounded p-3 flex justify-between items-start gap-3"
                            >

                                <div className="flex min-w-0 gap-3">

                                    <Icon
                                        className="h-5 w-5 text-orange-600 shrink-0 mt-1"
                                    />

                                    <div className="min-w-0">

                                        <p className="text-sm font-bold text-gray-800">

                                            {index + 1} - {item.title}

                                        </p>

                                        <p className="text-[10px] text-gray-600 mt-1">

                                            {getSessionName(
                                                item.sessionId
                                            )}

                                        </p>


                                        {item.description && (

                                            <p className="text-xs text-gray-600 mt-2 whitespace-pre-wrap">

                                                {item.description}

                                            </p>

                                        )}


                                        <div className="flex flex-wrap gap-2 mt-2">

                                            <span className="rounded-full bg-white px-2 py-1 text-[10px]">

                                                {item.contentType}

                                            </span>

                                            <span className="rounded-full bg-white px-2 py-1 text-[10px]">

                                                {item.contentProvider || "External"}

                                            </span>

                                            <span className="rounded-full bg-white px-2 py-1 text-[10px]">

                                                Prompts: {activePromptCount}

                                            </span>

                                            <span className="rounded-full bg-white px-2 py-1 text-[10px]">

                                                Order: {item.displayOrder ?? 0}

                                            </span>

                                            <span className={`rounded-full px-2 py-1 text-[10px] ${
                                                item.active !== false
                                                    ? "bg-lime-100 text-green-700"
                                                    : "bg-red-100 text-red-700"
                                            }`}>

                                                {item.active !== false
                                                    ? "Active"
                                                    : "Inactive"}

                                            </span>

                                        </div>

                                    </div>

                                </div>



                                {/*
                                |--------------------------------------------------------------------------
                                | Actions
                                |--------------------------------------------------------------------------
                                */}

                                <div className="flex items-center shrink-0">

                                    <PencilSquareIcon
                                        onClick={() =>
                                            onUpdate(item._id)
                                        }
                                        className="h-5 w-5 text-orange-600 cursor-pointer mx-2"
                                    />

                                    <TrashIcon
                                        onClick={() =>
                                            onDelete(item._id)
                                        }
                                        className="h-5 w-5 text-orange-600 cursor-pointer mx-2"
                                    />

                                </div>

                            </div>

                        )

                    })}

                </div>

            </div>


        </div>

    )

}


export default EtSessionReferenceContentList